import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Building2,
  Mail,
  User,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  Lock,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { TokenManager } from '../../lib/token-manager';
import { getApiErrorCode, getApiErrorMessage } from '../../utils/errorUtils';

/**
 * CR-7458: codes the accept endpoint returns when the link itself is spent
 * (used, cancelled or expired). No password can fix any of them.
 */
const SPENT_LINK_CODES = new Set([
  'invitation_expired',
  'invitation_already_processed',
  'invitation_cancelled',
  'invitation_not_valid',
]);

/**
 * Whether an accept failure refused the link rather than the form input.
 *
 * @param error - The error thrown by the accept request
 * @returns True when the server's error code marks the invitation as spent
 */
function isSpentLinkError(error: unknown): boolean {
  const code = getApiErrorCode(error);
  return code !== undefined && SPENT_LINK_CODES.has(code);
}

/** Where a newly accepted supplier starts: their onboarding queue (CR-4761). */
const MY_PROGRESS = '/onboarding/my-progress';

/** POST /onboarding/accept/<token>/ — a session, or an MFA challenge (CR-8632). */
interface AcceptInvitationResponse {
  message: string;
  /** Present only when the invitee's role does not require MFA. */
  token?: string;
  mfa_setup_required?: boolean;
  mfa_required?: boolean;
  /** Short-lived token for the MFA step pages; never a session. */
  mfa_session_token?: string;
  workflow_id: number;
  company: { id: number; name: string };
}

interface InvitationData {
  valid: boolean;
  error?: string;
  invitation_id?: number;
  company_name?: string;
  contact_name?: string;
  email?: string;
  customer_name?: string;
  questionnaires?: Array<{
    id: number;
    name: string;
    description: string;
    dataset_type: string;
  }>;
}

export default function AcceptSupplierInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (token) {
      verifyInvitation();
    }
  }, [token]);

  const verifyInvitation = async () => {
    try {
      const response = await api.get(`/onboarding/verify/${token}/`);
      setInvitation(response.data);
    } catch (error: unknown) {
      setInvitation({
        valid: false,
        error: getApiErrorMessage(error, 'Invalid or expired invitation'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!password) {
      toast.error('Please enter a password');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post<AcceptInvitationResponse>(`/onboarding/accept/${token}/`, {
        password,
      });
      const { data } = response;

      // CR-8632: a role that requires MFA gets an MFA challenge, not a
      // session. Hand the session token to the same step pages Login uses;
      // they issue the real token once MFA is complete, and the supplier then
      // lands on their onboarding queue.
      if (data.mfa_session_token && (data.mfa_setup_required || data.mfa_required)) {
        navigate(data.mfa_setup_required ? '/mfa-setup' : '/mfa-verify', {
          state: { sessionToken: data.mfa_session_token, from: { pathname: MY_PROGRESS } },
        });
        return;
      }
      if (!data.token) {
        throw new Error('Unexpected invitation accept response');
      }

      // Store auth token securely
      TokenManager.setToken(data.token);

      toast.success('Account created successfully! Welcome aboard.');

      // CR-4761: Send new suppliers straight to their onboarding queue. The
      // dashboard isn't populated for a just-created supplier account so the
      // old /dashboard redirect left them staring at an empty page.
      setTimeout(() => {
        window.location.href = MY_PROGRESS;
      }, 1500);
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Failed to accept invitation');
      // The link was used in another tab, cancelled or expired while the form
      // was open. Show the same card a refused link gets on load, not a
      // password form that can never succeed.
      if (isSpentLinkError(error)) {
        setInvitation({ valid: false, error: message });
      } else {
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 flex flex-col items-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Verifying invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 rounded-full bg-red-100">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-xl">Invalid Invitation</CardTitle>
            <CardDescription>
              {invitation?.error || 'This invitation link is invalid or has expired.'}
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Button variant="outline" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 rounded-full bg-green-100">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl">Welcome to Supplier Portal</CardTitle>
          <CardDescription>
            You've been invited by <strong>{invitation.customer_name}</strong> to join as a supplier
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Company Info */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Company</p>
                <p className="font-medium">{invitation.company_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Contact</p>
                <p className="font-medium">{invitation.contact_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{invitation.email}</p>
              </div>
            </div>
          </div>

          {/* Questionnaires to complete */}
          {invitation.questionnaires && invitation.questionnaires.length > 0 && (
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4" />
                Questionnaires to Complete
              </Label>
              <div className="space-y-2">
                {invitation.questionnaires.map((q) => (
                  <div key={q.id} className="flex items-center gap-2 text-sm p-2 bg-muted/30 rounded">
                    <Badge variant="outline">{q.dataset_type}</Badge>
                    <span>{q.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Create Password */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Create Your Account
            </h3>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password (min 8 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex-col gap-3">
          <Button
            className="w-full"
            size="lg"
            onClick={handleAccept}
            disabled={submitting || !password || password !== confirmPassword}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Account...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Accept Invitation & Create Account
              </>
            )}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            By accepting, you agree to our Terms of Service and Privacy Policy
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
